import React, { useState } from "react";
import { FieldArray, Formik, Form } from "formik";
import * as Yup from "yup";
import { Grid, Button, IconButton, Stack, Icon, Divider, CircularProgress, LinearProgress, Typography, Box } from "@mui/material";
import GenericInputFile from "../../atoms/Common/GenericInputFile";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

const validationSchema = Yup.object().shape({
  files: Yup.array().of(
    Yup.object().shape({
      fileConcept: Yup.string().required("Seleccionar concepto"),
      file: Yup.mixed().required("Seleccionar archivo"),
    })
  ),
});

const GenericFileUpload = ({ 
  fileNameOptions = [], 
  uploadFunction, 
  id, 
  onSubmit 
}) => {
  const { openSnack } = useSnack();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);

  const handleSubmit = async (values) => {
    if (isUploading) return; // Prevent multiple submissions
    
    setIsUploading(true);
    setUploadProgress(0);
    setCurrentFileIndex(0);
    setTotalFiles(values.files.length);
    
    try {
      for (let i = 0; i < values.files.length; i++) {
        const e = values.files[i];
        setCurrentFileIndex(i + 1);
        
        const payload = { fileConcept: e.fileConcept, id, fileCode: e.fileCode };
        try {
          await uploadFunction(payload, e.file);
          openSnack(`${e.fileConcept} subido exitosamente`, "success");
        } catch (e) {
          openSnack(handleError(e), "error");
        }
        
        // Update progress after each file
        const progress = ((i + 1) / values.files.length) * 100;
        setUploadProgress(progress);
      }
      onSubmit();
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentFileIndex(0);
      setTotalFiles(0);
    }
  };

  return (
    <Formik
      initialValues={{ files: [{ fileConcept: "", fileCode: "", file: null }] }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values }) => (
        <Form>
          <Grid container spacing={2} mr={2}>
            <Grid item xs={12}>
              <FieldArray name="files">
                {({ push, remove }) => (
                  <Grid container spacing={2} item xs={12} alignItems={"center"}>
                    {values.files.map((file, index) => (
                      <Grid item xs={12} key={index}>
                        <Stack direction={"row"}>
                          <GenericInputFile 
                            name={`files[${index}]`} 
                            fileNameOptions={fileNameOptions}
                            disabled={isUploading}
                          />
                          <IconButton
                            onClick={() => remove(index)}
                            disabled={values.files.length === 1 || isUploading}
                            color="error"
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                        </Stack>
                      </Grid>
                    ))}
                    <Grid item xs={12} alignItems={"center"}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => push({ fileConcept: "", fileCode: "", file: null })}
                        color="success"
                        disabled={isUploading}
                      >
                        <Icon>add</Icon>
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </FieldArray>
            </Grid>
            <Divider />
            {isUploading && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Subiendo archivos: {currentFileIndex} de {totalFiles}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {Math.round(uploadProgress)}% completado
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isUploading}
                onClick={() => {
                  const missingFiles = values.files.some(file => !file.file);
                  if (missingFiles) {
                    openSnack("No se han subido todos los archivos", "warning");
                  }
                }}
                startIcon={isUploading && <CircularProgress size={20} color="inherit" />}
              >
                {isUploading ? "Subiendo..." : "Guardar"}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default GenericFileUpload; 